"use client";

import { useEffect, useState } from "react";
import StatusDot from "@/components/StatusDot";
import DateRangePicker, { RangeWithPreset, defaultRange, comparacionLabel } from "@/components/DateRangePicker";

const LOC_RESTAURANTE = "L5P2T0W4JGRC6";
const LOC_TAPAS       = "LAJM3467NBMRD";
type LocalFilter = "todo" | typeof LOC_RESTAURANTE | typeof LOC_TAPAS;

// ── Etiquetas dinámicas según el preset ─────────────────────────────────────

const MESES_ES = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
function cap(s: string) { return s.charAt(0).toUpperCase() + s.slice(1); }

function labelVentas(presetKey: string, rango: RangeWithPreset): string {
  switch (presetKey) {
    case "hoy":       return "Ventas de hoy";
    case "semana":    return "Ventas de la semana";
    case "mes":       return "Ventas del mes";
    case "mes_pasado":{
      const d = new Date(rango.desde + "T00:00:00");
      return `Ventas de ${MESES_ES[d.getMonth()]}`;
    }
    case "anio":      return "Ventas del año";
    default: {
      // rango libre: mostrar fechas cortas
      const [, m1, d1] = rango.desde.split("-");
      const [, m2, d2] = rango.hasta.split("-");
      return `Ventas ${d1}/${m1} - ${d2}/${m2}`;
    }
  }
}

function labelSeccion(presetKey: string): string {
  switch (presetKey) {
    case "hoy":       return "Pulso de hoy";
    case "semana":    return "Pulso de la semana";
    case "mes":       return "Pulso del mes";
    case "mes_pasado":return "Pulso del mes pasado";
    case "anio":      return "Pulso del año";
    default:          return "Pulso del periodo";
  }
}

function labelBanner(presetKey: string, rango: RangeWithPreset): string {
  switch (presetKey) {
    case "hoy":       return "Hoy";
    case "semana":    return "Esta semana";
    case "mes":       { const now = new Date(); return cap(MESES_ES[now.getMonth()]); }
    case "mes_pasado":{ const d = new Date(rango.desde + "T00:00:00"); return cap(MESES_ES[d.getMonth()]); }
    case "anio":      return String(new Date().getFullYear());
    default: {
      const [, m1, d1] = rango.desde.split("-");
      return `${d1}/${m1}`;
    }
  }
}

function labelVsAnterior(presetKey: string, rango: RangeWithPreset): string {
  switch (presetKey) {
    case "hoy":       return "vs ayer";
    case "semana":    return "vs semana pasada";
    case "mes":       { const prev = new Date(); prev.setMonth(prev.getMonth()-1); return `vs ${MESES_ES[prev.getMonth()]} a estas alturas`; }
    case "mes_pasado":{ const d = new Date(rango.desde + "T00:00:00"); d.setMonth(d.getMonth()-1); return `vs ${MESES_ES[d.getMonth()]}`; }
    case "anio":      return "vs año anterior";
    default:          return "vs periodo anterior";
  }
}

// ── Componente selector local ────────────────────────────────────────────────

function LocalSelector({ value, onChange }: { value: LocalFilter; onChange: (v: LocalFilter) => void }) {
  const opts: { key: LocalFilter; label: string }[] = [
    { key: "todo",          label: "Todo"        },
    { key: LOC_RESTAURANTE, label: "Restaurante" },
    { key: LOC_TAPAS,       label: "Tapas"       },
  ];
  return (
    <div style={{ display: "inline-flex", background: "rgba(255,255,255,.06)", borderRadius: "10px", padding: "3px", gap: "2px" }}>
      {opts.map(o => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          style={{
            padding: "5px 13px", borderRadius: "7px", border: "none",
            fontSize: "12px", fontWeight: value === o.key ? 700 : 400,
            cursor: "pointer", transition: "all 150ms", fontFamily: "inherit",
            background: value === o.key ? "var(--acc)" : "transparent",
            color: value === o.key ? "#fff" : "var(--tx3)",
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── Gráfico de barras con tooltip ───────────────────────────────────────────

function ChartBars({ data }: { data: any[] }) {
  const [hovered, setHovered] = useState<number | null>(null);
  if (!data?.length) return <div className="empty">Sin datos de ventas</div>;

  const CHART_H   = 110;
  const MIN_H     = 4;
  const mesActualIdx = new Date().getMonth(); // 0-based, coincide con índice del array de 12 meses
  const maxVentas = Math.max(...data.map((x: any) => x.ventas), 1);

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '140px', width: '100%', position: 'relative' }}>
      {data.map((m: any, idx: number) => {
        const esActual  = idx === mesActualIdx;
        const esFuturo  = idx > mesActualIdx;
        const rawH      = (m.ventas / maxVentas) * CHART_H;
        const barH      = m.ventas > 0 ? Math.max(rawH, MIN_H) : (esFuturo ? 0 : 0);
        const isHovered = hovered === idx;

        return (
          <div
            key={m.mes}
            onMouseEnter={() => setHovered(idx)}
            onMouseLeave={() => setHovered(null)}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'flex-end',
              gap: '4px', height: '100%', position: 'relative', cursor: 'default',
            }}
          >
            {/* Tooltip */}
            {isHovered && (
              <div style={{
                position: 'absolute', bottom: 'calc(100% - 16px)',
                left: '50%', transform: 'translateX(-50%)',
                background: 'var(--panel)', border: '1px solid var(--bd)',
                borderRadius: '8px', padding: '7px 10px',
                fontSize: '12px', whiteSpace: 'nowrap', zIndex: 10,
                boxShadow: '0 4px 16px rgba(0,0,0,.25)',
                pointerEvents: 'none',
              }}>
                <div style={{ fontWeight: 700, color: 'var(--tx)', marginBottom: '1px' }}>{m.mes}</div>
                <div style={{ color: m.ventas > 0 ? 'var(--ok)' : 'var(--tx3)' }}>
                  {m.ventas > 0 ? `€${m.ventas.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : 'Sin datos'}
                </div>
              </div>
            )}

            {/* Barra */}
            <div style={{
              width: '100%',
              height: esFuturo ? '2px' : `${barH}px`,
              minHeight: (barH > 0 && !esFuturo) ? `${MIN_H}px` : undefined,
              background: esFuturo
                ? 'var(--bd)'
                : esActual
                  ? 'var(--acc)'
                  : isHovered ? 'var(--acc)' : 'var(--tx3)',
              borderRadius: esFuturo ? '2px' : '4px 4px 0 0',
              opacity: esFuturo ? 0.3 : esActual ? 1 : isHovered ? 0.9 : 0.65,
              transition: 'background 150ms, opacity 150ms',
              flexShrink: 0,
            }} />

            {/* Etiqueta mes */}
            <div style={{
              fontSize: '9px', fontWeight: esActual ? 700 : 500,
              color: esActual ? 'var(--acc)' : 'var(--tx3)',
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              {m.mes.slice(0, 3)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Constantes y helpers ─────────────────────────────────────────────────────

const API = process.env.NEXT_PUBLIC_API_URL || "http://100.68.12.86:3200";

async function jget(u: string) {
  const r = await fetch(u, { cache: "no-store" });
  if (!r.ok) throw new Error(u + " " + r.status);
  return r.json();
}

type Project = {
  id: number;
  name: string;
  status: string;
  task_count: number;
  tasks_done: number;
};

// ── Componente principal ─────────────────────────────────────────────────────

export default function HomePage() {
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [kpisData, setKpisData] = useState<any>(null);
  const [porLocal, setPorLocal] = useState<any>(null);
  const [finanzasData, setFinanzasData] = useState<any>(null);
  const [pendingDrafts, setPendingDrafts] = useState(0);
  const [pendingReviews, setPendingReviews] = useState(0);
  const [projects, setProjects] = useState<Project[]>([]);
  const [clock, setClock] = useState("");
  const [filtro, setFiltro] = useState<LocalFilter>("todo");
  const [rango, setRango] = useState<RangeWithPreset>(defaultRange());

  useEffect(() => {
    setLoading(true);
    Promise.all([
      jget(`${API}/kpis?desde=${rango.desde}&hasta=${rango.hasta}`).catch(() => null),
      jget(`${API}/finanzas`).catch(() => null),
      jget(`${API}/correo/borradores`).catch(() => []),
      jget(`${API}/marketing/reviews/pending-count`).catch(() => ({ count: 0 })),
      jget(`${API}/projects`).catch(() => []),
    ]).then(([kpis, fin, drafts, reviews, projs]) => {
      setKpisData(kpis);
      setPorLocal(kpis?.porLocal ?? null);
      setFinanzasData(fin);
      setPendingDrafts(Array.isArray(drafts) ? drafts.filter((d: any) => d.estado !== 'enviado').length : 0);
      setPendingReviews(reviews.count || 0);
      setProjects(Array.isArray(projs) ? projs.filter((p: any) => p.status === 'active') : []);
      setLoading(false);
      setInitialized(true);
    });

    const tick = () => {
      const n = new Date();
      setClock(`${String(n.getHours()).padStart(2, "0")}:${String(n.getMinutes()).padStart(2, "0")}`);
    };
    tick();
    const id = setInterval(tick, 10000);
    return () => clearInterval(id);
  }, [rango]);

  // Primera carga: esperar datos. En recargas por cambio de rango/filtro: mostrar datos anteriores con overlay sutil.
  if (!initialized) {
    return (
      <div style={{ padding: "40px", color: "var(--tx2)" }}>
        <div>Cargando dashboard...</div>
      </div>
    );
  }

  // ── Métricas de venta según filtro local ────────────────────────────────
  const loc         = filtro !== "todo" ? porLocal?.[filtro] : null;
  const totalVentas = filtro === "todo" ? (kpisData?.totalVentas ?? 0) : (loc?.ventasMes ?? 0);
  const variacion   = filtro === "todo" ? (kpisData?.variacion ?? 0)   : (loc?.variacion ?? 0);
  const ventasPorMes = kpisData?.ventasPorMes ?? [];
  // Proyección: solo válida para "Este mes"
  const proyeccion   = rango.presetKey === "mes" ? (kpisData?.proyeccion ?? null) : null;

  const gastoComensal: any[] = filtro === "todo"
    ? [porLocal?.[LOC_RESTAURANTE], porLocal?.[LOC_TAPAS]].filter(Boolean)
    : [loc].filter(Boolean);

  // ── Etiquetas dinámicas ──────────────────────────────────────────────────
  const presetKey     = rango.presetKey;
  const textoVentas   = labelVentas(presetKey, rango);
  const textoSeccion  = labelSeccion(presetKey);
  const textoBanner   = labelBanner(presetKey, rango);
  const textoVsAnterior = labelVsAnterior(presetKey, rango);
  const esMesActual   = presetKey === "mes";

  // Datos del objetivo (solo para "Este mes")
  const pctObjetivo   = proyeccion?.pctObjetivo ?? 0;
  const objetivoMes   = proyeccion?.objetivoMes ?? 0;
  const proyeccionMes = proyeccion?.proyeccionMes ?? 0;
  const diaActual     = proyeccion?.diaActual ?? 1;
  const diasEnMes     = proyeccion?.diasEnMes ?? 30;
  const pctMesTranscurrido = (diaActual / diasEnMes) * 100;
  const aRitmo        = pctObjetivo >= pctMesTranscurrido;

  // Comensales del periodo (suma de los locales filtrados)
  const coversTotal: number | null = (() => {
    const fuentes = filtro === "todo"
      ? [porLocal?.[LOC_RESTAURANTE], porLocal?.[LOC_TAPAS]]
      : [loc];
    const vals = fuentes.map((l: any) => l?.covers ?? null);
    if (vals.every((v: any) => v === null)) return null;
    return vals.reduce((s: number, v: any) => s + (v ?? 0), 0);
  })();

  const labelComensales = (() => {
    switch (presetKey) {
      case "hoy":       return "Comensales hoy";
      case "semana":    return "Comensales esta semana";
      case "mes":       return "Comensales del mes";
      case "mes_pasado":return `Comensales de ${labelBanner(presetKey, rango).toLowerCase()}`;
      case "anio":      return "Comensales del año";
      default:          return "Comensales del periodo";
    }
  })();

  // Facturas próximas (siempre global, no depende del rango)
  const facturasProximas = finanzasData?.porPagar
    ? finanzasData.porPagar.filter((f: any) => {
        if (!f.vencimiento) return false;
        const venc = new Date(f.vencimiento);
        const hoy  = new Date();
        const diffDays = Math.ceil((venc.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
        return diffDays >= 0 && diffDays <= 7;
      }).length
    : 0;

  // Nota de tendencia (siempre sobre el año, no depende del rango)
  let notaTendencia = "";
  if (ventasPorMes.length >= 3) {
    const ultimos3 = ventasPorMes.slice(-3);
    const esAlza = ultimos3.every((m: any, i: number) => i === 0 || m.ventas >= ultimos3[i - 1].ventas);
    if (esAlza) {
      notaTendencia = `Llevas ${ultimos3.length} meses al alza`;
    } else {
      const mejor = ventasPorMes.reduce((max: any, m: any) => m.ventas > max.ventas ? m : max, ventasPorMes[0]);
      notaTendencia = `${mejor.mes} fue tu mejor mes (€${mejor.ventas.toLocaleString('es-ES')})`;
    }
  }

  const decisiones = pendingDrafts + pendingReviews + facturasProximas;

  return (
    <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity 200ms ease', position: 'relative' }}>
      {loading && (
        <div style={{
          position: 'absolute', top: 0, right: 0,
          fontSize: '11px', color: 'var(--tx3)', display: 'flex', alignItems: 'center', gap: '5px',
        }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--acc)', display: 'inline-block', animation: 'pulse 1s ease-in-out infinite' }} />
          actualizando
        </div>
      )}
      <div className="top">
        <div className="brand">VARADERO <b>HQ</b></div>
        <div className="topr">
          <span className="live"><StatusDot status="ok" pulse /> SISTEMAS OK</span>
          <span>{clock}</span>
        </div>
      </div>

      {/* Selectores local + fechas - solo afectan bloque de venta */}
      <div style={{ marginBottom: "16px", display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "flex-start" }}>
        <LocalSelector value={filtro} onChange={setFiltro} />
        <DateRangePicker value={rango} onChange={setRango} />
      </div>

      {/* Banner veredicto */}
      <div className="hero-ceo">
        <div className="veredicto">
          <span className="mes">{textoBanner}:</span>{" "}
          <span className="chip-inline" style={{ background: variacion >= 0 ? 'rgba(63,208,127,.2)' : 'rgba(255,107,107,.2)', color: variacion >= 0 ? 'var(--ok)' : 'var(--bad)' }}>
            €{totalVentas.toLocaleString('es-ES')}
          </span>{" "}
          <span className="sep">·</span>{" "}
          <span className="chip-inline" style={{ background: variacion >= 0 ? 'rgba(63,208,127,.2)' : 'rgba(255,107,107,.2)', color: variacion >= 0 ? 'var(--ok)' : 'var(--bad)' }}>
            {variacion >= 0 ? '+' : ''}{variacion}%
          </span>{" "}
          <span className="txt">{textoVsAnterior}</span>{" "}
          {esMesActual && (
            <>
              <span className="sep">·</span>{" "}
              <span className="chip-inline" style={{ background: aRitmo ? 'rgba(246,178,74,.2)' : 'rgba(255,107,107,.2)', color: aRitmo ? 'var(--warn)' : 'var(--bad)' }}>
                {pctObjetivo.toFixed(0)}%
              </span>{" "}
              <span className="txt">del objetivo (€{objetivoMes.toLocaleString('es-ES')})</span>{" "}
              <span className="sep">·</span>{" "}
              <span className="estado">{aRitmo ? 'a ritmo' : 'por debajo'}</span>
            </>
          )}
        </div>
      </div>

      {/* Pulso del periodo */}
      <div className="section-label">{textoSeccion}</div>
      <div className="grid-ceo">
        <div className="card-ceo">
          <div className="lbl-ceo">{textoVentas}</div>
          <div className="v-ceo">€{totalVentas.toLocaleString('es-ES')}</div>
          <div className={`delta-ceo ${variacion >= 0 ? 'up' : 'down'}`}>
            {variacion >= 0 ? '↑' : '↓'} {Math.abs(variacion)}% {textoVsAnterior}
          </div>
        </div>

        {/* Comensales del periodo */}
        <div className="card-ceo">
          <div className="lbl-ceo">{labelComensales}</div>
          {coversTotal !== null ? (
            <>
              <div className="v-ceo">{coversTotal.toLocaleString('es-ES')}</div>
              {filtro === "todo" && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '6px' }}>
                  {[porLocal?.[LOC_RESTAURANTE], porLocal?.[LOC_TAPAS]].filter(Boolean).map((l: any) => (
                    l?.covers != null ? (
                      <div key={l.nombre} style={{ fontSize: '11px', color: 'var(--tx3)', display: 'flex', justifyContent: 'space-between' }}>
                        <span>{l.nombre}</span>
                        <span style={{ fontWeight: 600, color: 'var(--tx2)' }}>{l.covers.toLocaleString('es-ES')}</span>
                      </div>
                    ) : null
                  ))}
                </div>
              )}
              {totalVentas > 0 && coversTotal > 0 && (
                <div className="delta-ceo up" style={{ marginTop: '8px' }}>
                  €{(totalVentas / coversTotal).toFixed(2)} / comensal
                </div>
              )}
            </>
          ) : (
            <>
              <div className="v-ceo empty-val">—</div>
              <div className="help-txt">Sin datos de comensales</div>
            </>
          )}
        </div>

        <div className="card-ceo">
          <div className="lbl-ceo">Gasto / comensal</div>
          {gastoComensal.map((gl: any) =>
            gl ? (
              <div key={gl.nombre} className="gasto-local">
                {filtro === "todo" && <span className="gasto-local-nombre">{gl.nombre}</span>}
                {gl.gastoPorComensal !== null ? (
                  <>
                    <span className="gasto-local-valor">€{gl.gastoPorComensal.toFixed(2)}</span>
                    {gl.varGastoComensal !== null && (
                      <span className={`delta-ceo ${gl.varGastoComensal >= 0 ? 'up' : 'down'}`}>
                        {gl.varGastoComensal >= 0 ? '↑' : '↓'} {Math.abs(gl.varGastoComensal)}% {textoVsAnterior}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="gasto-local-nd">- <span className="help-txt">sin datos de comensales</span></span>
                )}
              </div>
            ) : null
          )}
        </div>
      </div>

      {/* Tendencia (siempre el año completo, independiente del rango) */}
      <div className="section-label">Tendencia anual</div>
      <div className="grid-tendencia">
        <div className="card-ceo chart-card">
          <ChartBars data={ventasPorMes} />
        </div>

        <div className="card-ceo nota-card">
          <div className="lbl-ceo">Nota de tendencia</div>
          <div className="nota-txt">
            {notaTendencia || 'Pendiente de datos suficientes'}
          </div>
        </div>
      </div>

      {/* Rentabilidad */}
      <div className="section-label">Rentabilidad</div>
      <div className="grid-rent">
        <div className="card-ceo constr">
          <div className="badge-constr">Pendiente de conectar datos</div>
          <div className="lbl-ceo">Margen</div>
          <div className="v-ceo empty-val">-</div>
          <div className="help-txt">Margen real - se activará al conectar Holded</div>
        </div>
        <div className="card-ceo constr">
          <div className="badge-constr">Pendiente de conectar datos</div>
          <div className="lbl-ceo">Coste de personal</div>
          <div className="v-ceo empty-val">-</div>
          <div className="help-txt">% sobre ventas - se activará al conectar nóminas</div>
        </div>
      </div>

      {/* Lo que requiere tu atención */}
      <div className="section-label">Lo que requiere tu atención</div>
      <div className="grid-didac">
        <div className="card-ceo dec-card">
          <div className="ch-ceo">
            <h3 className="h3-ceo">
              <StatusDot status={decisiones > 0 ? "warn" : "ok"} /> Decisiones pendientes
            </h3>
            <span className="badge-count">{decisiones}</span>
          </div>
          {decisiones === 0 ? (
            <div className="empty-dec">Todo al día. Nada pendiente de tu firma.</div>
          ) : (
            <div className="dec-list">
              {pendingDrafts > 0 && (
                <a href="/inbox" className="dec-item">
                  <div className="dec-icon">📧</div>
                  <div className="dec-txt"><b>{pendingDrafts}</b> {pendingDrafts === 1 ? 'borrador' : 'borradores'} de correo</div>
                </a>
              )}
              {facturasProximas > 0 && (
                <a href="/finanzas" className="dec-item">
                  <div className="dec-icon">💰</div>
                  <div className="dec-txt"><b>{facturasProximas}</b> {facturasProximas === 1 ? 'factura vence' : 'facturas vencen'} en 7 días</div>
                </a>
              )}
              {pendingReviews > 0 && (
                <a href="/inbox" className="dec-item">
                  <div className="dec-icon">⭐</div>
                  <div className="dec-txt"><b>{pendingReviews}</b> {pendingReviews === 1 ? 'reseña' : 'reseñas'} sin responder</div>
                </a>
              )}
            </div>
          )}
        </div>

        <div className="card-ceo proj-card">
          <div className="ch-ceo">
            <h3 className="h3-ceo">
              <StatusDot status="run" pulse /> Proyectos estratégicos
            </h3>
            <span className="badge-count">{projects.length}</span>
          </div>
          {projects.length === 0 ? (
            <div className="empty-proj">No hay proyectos activos</div>
          ) : (
            <div className="proj-list">
              {projects.map((p) => {
                const pctAvance = p.task_count > 0 ? (p.tasks_done / p.task_count) * 100 : 0;
                return (
                  <div key={p.id} className="proj-item">
                    <div className="proj-header">
                      <div className="proj-name">{p.name}</div>
                      <div className="proj-pct">{pctAvance.toFixed(0)}%</div>
                    </div>
                    <div className="proj-bar">
                      <div className="proj-fill" style={{ width: `${pctAvance}%` }} />
                    </div>
                    <div className="proj-tasks">
                      {p.tasks_done} / {p.task_count} {p.task_count === 1 ? 'tarea' : 'tareas'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
